import React, { useEffect, useMemo, useState } from 'react';
import * as THREE from 'three';
import { SceneState, useSceneStore } from '../../state/sceneStore';
import { loadBinPointCloud } from '../../utils/loadBinPointCloud';
import { filterPointsByHeight } from '../../utils/pointCloudHeightFilter';
import {
  registerCloudPending,
  registerCloudReady,
  unregisterCloud,
} from '../../utils/cloudLoading';
import {
  ACTIVE_ALIGNMENT_CLOUD_COLOR,
  alignedSensorColor,
} from '../../utils/alignmentPointCloudColors';
import { useZScale } from './SceneCanvas';

export { ACTIVE_ALIGNMENT_CLOUD_COLOR };

interface AlignmentPointCloudProps {
  url: string;
  sensorId: string;
  /** Selected sensor being actively aligned — rendered larger and in a bold color. */
  isActiveAlignment?: boolean;
}

const disablePointCloudRaycast = () => null;

const AlignmentPointCloud: React.FC<AlignmentPointCloudProps> = ({
  url,
  sensorId,
  isActiveAlignment = false,
}) => {
  const pointSize = useSceneStore((state: SceneState) => state.sceneOptions.pointSize);
  const useOrthographic = useSceneStore((state: SceneState) => state.useOrthographic);
  const alignmentHeightMin = useSceneStore((state: SceneState) => state.alignmentHeightMin);
  const alignmentHeightMax = useSceneStore((state: SceneState) => state.alignmentHeightMax);
  const sensorPosition = useSceneStore((state: SceneState) =>
    state.sensors.find((item) => item.id === sensorId)?.position ?? null
  );
  const sensorRotation = useSceneStore((state: SceneState) =>
    state.sensors.find((item) => item.id === sensorId)?.rotation ?? null
  );
  const zScale = useZScale();
  const [points, setPoints] = useState<Float32Array | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoadError(null);
    setPoints(null);
    registerCloudPending('align', sensorId);

    void loadBinPointCloud(url)
      .then((loaded) => {
        if (!cancelled) {
          setPoints(loaded);
        }
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          const message = error instanceof Error ? error.message : 'Failed to load alignment cloud';
          setLoadError(message);
        }
      })
      .finally(() => {
        if (!cancelled) {
          registerCloudReady('align', sensorId);
        }
      });

    return () => {
      cancelled = true;
      unregisterCloud('align', sensorId);
    };
  }, [url, sensorId]);

  const sensorPose = useMemo(() => {
    if (!sensorPosition || !sensorRotation) {
      return null;
    }
    return {
      position: sensorPosition as [number, number, number],
      rotation: sensorRotation as [number, number, number],
    };
  }, [sensorPosition, sensorRotation]);

  const geometry = useMemo(() => {
    if (!points || points.length === 0) {
      return null;
    }
    const filtered = filterPointsByHeight(
      points,
      alignmentHeightMin,
      alignmentHeightMax,
      sensorPose,
    );
    if (filtered.length === 0) {
      return null;
    }
    const geom = new THREE.BufferGeometry();
    geom.setAttribute('position', new THREE.BufferAttribute(filtered.slice(), 3));
    geom.computeBoundingSphere();
    return geom;
  }, [points, alignmentHeightMin, alignmentHeightMax, sensorPose]);

  useEffect(() => () => geometry?.dispose(), [geometry]);

  if (loadError || !geometry) {
    return null;
  }

  const baseMaterialSize = pointSize * Math.sqrt(zScale) * (useOrthographic ? 0.5 : 0.03);
  const materialSize = isActiveAlignment ? baseMaterialSize * 2.25 : baseMaterialSize;
  const color = isActiveAlignment ? ACTIVE_ALIGNMENT_CLOUD_COLOR : alignedSensorColor(sensorId);

  return (
    <points
      geometry={geometry}
      raycast={disablePointCloudRaycast}
      renderOrder={isActiveAlignment ? 10 : 0}
    >
      <pointsMaterial
        size={materialSize}
        color={color}
        transparent={false}
        opacity={1}
        depthTest
        depthWrite
      />
    </points>
  );
};

export default AlignmentPointCloud;
